import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import CheckIcon from '@material-ui/icons/Check';
import AutoIcon from '@material-ui/icons/BrightnessAuto';
import { InfoCard } from '@backstage/core-components';
import { useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import useObservable from 'react-use/esm/useObservable';

interface ThemePreviewColors {
  navBg: string;
  navText: string;
  bg: string;
  primary: string;
  accent: string;
}

// Visual preview colors for each registered theme ID.
const THEME_COLORS: Record<string, ThemePreviewColors> = {
  light: {
    navBg: '#004355',
    navText: '#ffffff',
    bg: '#f5f5f5',
    primary: '#00677f',
    accent: '#ffff40',
  },
  dark: {
    navBg: '#0b1623',
    navText: '#ffffff',
    bg: '#102033',
    primary: '#79aebf',
    accent: '#5097ab',
  },
  'high-contrast-light': {
    navBg: '#001a26',
    navText: '#ffffff',
    bg: '#ffffff',
    primary: '#003040',
    accent: '#ffff40',
  },
  'warm-sand': {
    navBg: '#3d2b1f',
    navText: '#f0e8e0',
    bg: '#faf8f5',
    primary: '#7c5c4a',
    accent: '#c8956b',
  },
  'ocean-blue': {
    navBg: '#1e3a5f',
    navText: '#ffffff',
    bg: '#f0f4f8',
    primary: '#2d5a8e',
    accent: '#a8d8f0',
  },
  'slate-dimmed': {
    navBg: '#1c2128',
    navText: '#adbac7',
    bg: '#22272e',
    primary: '#539bf5',
    accent: '#539bf5',
  },
  'hacker-terminal': {
    navBg: '#050505',
    navText: '#00ff41',
    bg: '#0d0d0d',
    primary: '#00ff41',
    accent: '#00ff41',
  },
  'dusk-purple': {
    navBg: '#12061e',
    navText: '#f0abfc',
    bg: '#1a0a2e',
    primary: '#c084fc',
    accent: '#e879f9',
  },
  sunrise: {
    navBg: '#c84400',
    navText: '#ffffff',
    bg: '#fff8f0',
    primary: '#e8420a',
    accent: '#ffcc44',
  },
  forest: {
    navBg: '#1a3d1a',
    navText: '#e0f0e0',
    bg: '#f0f7f0',
    primary: '#2d6e2d',
    accent: '#9ecc9e',
  },
};

const FALLBACK_COLORS: ThemePreviewColors = {
  navBg: '#37474f',
  navText: '#ffffff',
  bg: '#eceff1',
  primary: '#607d8b',
  accent: '#b0bec5',
};

const AUTO_COLORS: ThemePreviewColors = {
  navBg: '#455a64',
  navText: '#ffffff',
  bg: '#eceff1',
  primary: '#78909c',
  accent: '#cfd8dc',
};

const useStyles = makeStyles(theme => ({
  cardGrid: {
    padding: theme.spacing(1),
  },
  themeCard: {
    cursor: 'pointer',
    borderRadius: 10,
    overflow: 'hidden',
    border: '3px solid transparent',
    transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    userSelect: 'none',
    outline: 'none',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.22)',
    },
    '&:focus-visible': {
      outline: '2px solid currentColor',
      outlineOffset: 2,
    },
  },
  cardBody: {
    padding: '12px 14px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  swatchRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  swatch: {
    width: 13,
    height: 13,
    borderRadius: '50%',
    border: '1.5px solid rgba(255,255,255,0.3)',
    flexShrink: 0,
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },
  cardTitle: {
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    lineHeight: 1.3,
  },
  checkBadge: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionLabel: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    opacity: 0.6,
  },
}));

interface ThemeCardProps {
  id: string;
  title: string;
  colors: ThemePreviewColors;
  isActive: boolean;
  onSelect: () => void;
  autoIcon?: boolean;
}

function ThemeCard({ title, colors, isActive, onSelect, autoIcon }: ThemeCardProps) {
  const classes = useStyles();
  const borderColor = isActive ? colors.accent : 'transparent';
  const boxShadow = isActive
    ? `0 0 0 1px ${colors.accent}40, 0 4px 16px rgba(0,0,0,0.18)`
    : '0 2px 8px rgba(0,0,0,0.12)';

  return (
    <Tooltip title={`Switch to ${title}`} placement="top" arrow>
      <div
        role="button"
        tabIndex={0}
        aria-label={`${title} theme${isActive ? ' (active)' : ''}`}
        aria-pressed={isActive}
        className={classes.themeCard}
        style={{ background: colors.navBg, border: `3px solid ${borderColor}`, boxShadow }}
        onClick={onSelect}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') onSelect();
        }}
      >
        <div className={classes.cardBody}>
          <div className={classes.swatchRow}>
            <div className={classes.swatch} style={{ background: colors.bg }} />
            <div className={classes.swatch} style={{ background: colors.primary }} />
            <div className={classes.swatch} style={{ background: colors.accent }} />
          </div>
          <div className={classes.titleRow}>
            <Typography className={classes.cardTitle} style={{ color: colors.navText }}>
              {title}
            </Typography>
            {isActive && (
              <div
                className={classes.checkBadge}
                style={{ background: `${colors.accent}33` }}
                aria-hidden
              >
                {autoIcon ? (
                  <AutoIcon style={{ color: colors.accent, fontSize: 12 }} />
                ) : (
                  <CheckIcon style={{ color: colors.accent, fontSize: 12 }} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Tooltip>
  );
}

/** Renders all installed Backstage themes as a grid of themed, clickable preview cards. */
export function ThemeSelectorCard() {
  const classes = useStyles();
  const appThemeApi = useApi(appThemeApiRef);
  const activeThemeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );
  const themes = appThemeApi.getInstalledThemes();
  const isAuto = activeThemeId === undefined;

  return (
    <InfoCard title="Theme" variant="gridItem">
      <div className={classes.cardGrid}>
        <Typography className={classes.sectionLabel} component="p">
          Select a theme
        </Typography>
        <Grid container spacing={2}>
          {themes.map(theme => (
            <Grid item key={theme.id} xs={6} sm={4} md={3}>
              <ThemeCard
                id={theme.id}
                title={theme.title ?? theme.id}
                colors={THEME_COLORS[theme.id] ?? FALLBACK_COLORS}
                isActive={activeThemeId === theme.id}
                onSelect={() => appThemeApi.setActiveThemeId(theme.id)}
              />
            </Grid>
          ))}
          <Grid item xs={6} sm={4} md={3}>
            <ThemeCard
              id="auto"
              title="Auto"
              colors={AUTO_COLORS}
              isActive={isAuto}
              onSelect={() => appThemeApi.setActiveThemeId(undefined)}
              autoIcon
            />
          </Grid>
        </Grid>
      </div>
    </InfoCard>
  );
}
